/**
 * TransferGipModal — передача проекта другому ГИПу.
 * Режимы:
 *   • Постоянная передача — меняет основного ГИПа (chief_engineer_id)
 *   • На период отсутствия — устанавливает замещающего ГИПа (substitute_gip_id)
 */
import { useEffect, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { projectsApi } from '@/api/projects'
import { usersApi } from '@/api/users'
import { X, UserCheck } from 'lucide-react'
import type { ProjectOut } from '@/types'

interface Props {
  project: ProjectOut
  onClose: () => void
}

export default function TransferGipModal({ project, onClose }: Props) {
  const qc          = useQueryClient()
  const overlayRef  = useRef<HTMLDivElement>(null)
  const [mode, setMode]       = useState<'permanent' | 'substitute'>('permanent')
  const [selectedGip, setSelectedGip] = useState<string>('')

  const { data: users = [] } = useQuery({
    queryKey: ['users-assignable'],
    queryFn:  () => usersApi.assignable().then((r) => r.data),
  })
  const gips = users.filter((u) => u.role === 'chief_engineer' && u.is_active)

  const mutation = useMutation({
    mutationFn: () => projectsApi.transferGip(project.id, {
      gip_id:       selectedGip || null,
      is_substitute: mode === 'substitute',
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project', project.id] })
      qc.invalidateQueries({ queryKey: ['projects'] })
      onClose()
    },
  })

  // Предзаполнить по режиму
  useEffect(() => {
    if (mode === 'permanent') setSelectedGip(project.chief_engineer_id ?? '')
    else setSelectedGip(project.substitute_gip_id ?? '')
  }, [mode, project])

  function handleOverlayClick(e: React.MouseEvent) {
    if (e.target === overlayRef.current) onClose()
  }
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', h)
    return () => document.removeEventListener('keydown', h)
  }, [onClose])

  const inputCls = 'block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500'

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
    >
      <div className="w-full max-w-md mx-4 rounded-xl bg-white shadow-xl border border-gray-200">
        {/* Шапка */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <UserCheck className="h-5 w-5 text-brand-600" />
            <div>
              <h2 className="font-semibold text-gray-900">Передача ГИПа</h2>
              <p className="text-xs text-gray-500 mt-0.5">{project.code} · {project.name}</p>
            </div>
          </div>
          <button onClick={onClose} className="rounded-lg p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Текущие ГИПы */}
          {(project.chief_engineer || project.substitute_gip) && (
            <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 text-sm space-y-1">
              {project.chief_engineer && (
                <div className="text-gray-700">
                  <span className="font-medium">Основной ГИП:</span>{' '}
                  {project.chief_engineer.full_name}
                </div>
              )}
              {project.substitute_gip && (
                <div className="text-amber-700">
                  <span className="font-medium">Замещающий ГИП:</span>{' '}
                  {project.substitute_gip.full_name}
                </div>
              )}
            </div>
          )}

          {/* Режим передачи */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Вид передачи</p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { val: 'permanent',  label: 'Постоянная',       desc: 'Смена основного ГИПа' },
                { val: 'substitute', label: 'На время отсутствия', desc: 'Замещающий ГИП' },
              ].map(({ val, label, desc }) => (
                <button
                  key={val}
                  type="button"
                  onClick={() => setMode(val as typeof mode)}
                  className={`rounded-lg border px-3 py-2.5 text-left transition-colors ${
                    mode === val
                      ? 'border-brand-500 bg-brand-50 text-brand-700'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="text-sm font-medium">{label}</div>
                  <div className="text-xs opacity-75 mt-0.5">{desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Выбор ГИПа */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {mode === 'permanent' ? 'Новый основной ГИП' : 'Замещающий ГИП'}
            </label>
            <select
              value={selectedGip}
              onChange={(e) => setSelectedGip(e.target.value)}
              className={inputCls}
            >
              <option value="">
                {mode === 'substitute' ? ' - снять замещающего - ' : ' - без ГИПа - '}
              </option>
              {gips.map((u) => (
                <option key={u.id} value={u.id}>{u.full_name}</option>
              ))}
            </select>
            {mode === 'substitute' && (
              <p className="mt-1.5 text-xs text-gray-400">
                Замещающий ГИП получает те же права, что и основной.
                Чтобы снять замещение - выберите « - снять замещающего - ».
              </p>
            )}
          </div>

          {mutation.isError && (
            <div className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-700">
              Ошибка при сохранении. Попробуйте ещё раз.
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending}
              className="flex-1 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50 transition-colors"
            >
              {mutation.isPending ? 'Сохранение...' : 'Применить'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Отмена
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
